/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: ImovelParadigma_QuickUpload
 * VERSÃO: 1.0.0
 * ------------------------------------------------------------
 * Botão flutuante para anexar documentos EM QUALQUER FASE.
 * ============================================================
 */

import React, { useRef, useState } from 'react';
import {
  UploadCloud, FileText, CheckCircle2, X, AlertTriangle,
} from 'lucide-react';

const TIPOS_QUICK = [
  { chave: 'peticao',   titulo: 'Petição Inicial / Procuração' },
  { chave: 'matricula', titulo: 'Matrícula atualizada' },
  { chave: 'iptu',      titulo: 'IPTU / cadastro fiscal' },
  { chave: 'planta',    titulo: 'Planta aprovada' },
  { chave: 'habiteSe',  titulo: 'Habite-se / complementar' },
];

export default function ImovelParadigma_QuickUpload({
  documentosAtuais = {},
  onUpload,
}) {
  const [aberto, setAberto] = useState(false);
  const [tipoEscolhido, setTipoEscolhido] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const [erro, setErro] = useState('');
  const fileRef = useRef(null);

  const enviados = TIPOS_QUICK.filter((t) => {
    const doc = documentosAtuais[t.chave];
    return doc && (doc.status === 'enviado' || doc.status === 'pendente' || doc.file || doc.meta);
  }).length;

  const abrir = () => {
    setAberto(true);
    setTipoEscolhido(null);
    setArquivo(null);
    setErro('');
  };

  const fechar = () => {
    setAberto(false);
    setTipoEscolhido(null);
    setArquivo(null);
    setErro('');
  };

  const selecionarArquivo = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setErro('Somente arquivos PDF são aceitos.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setErro('O arquivo excede 20 MB.');
      return;
    }
    setErro('');
    setArquivo(f);
  };

  const confirmar = () => {
    if (!tipoEscolhido) { setErro('Escolha o tipo de documento.'); return; }
    if (!arquivo) { setErro('Selecione um arquivo PDF.'); return; }
    if (typeof onUpload === 'function') {
      onUpload(tipoEscolhido, arquivo);
    }
    fechar();
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        title="Anexar documento"
        className="fixed bottom-6 right-6 z-[200] w-14 h-14 rounded-full bg-amber-400 text-slate-950 shadow-2xl hover:bg-white transition-all flex items-center justify-center group"
      >
        <UploadCloud size={22} />
        {enviados > 0 && (
          <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
            {enviados}
          </span>
        )}
      </button>

      {aberto && (
        <div className="fixed inset-0 z-[300] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="bg-slate-900 w-full max-w-lg rounded-3xl border border-white/10 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">

            <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-white/5 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center">
                  <UploadCloud size={18} className="text-slate-950" />
                </div>
                <div>
                  <h3 className="text-white text-[14px] font-black uppercase italic tracking-tight leading-none">
                    Anexar Documento
                  </h3>
                  <p className="text-[8px] text-amber-400/70 font-black uppercase tracking-widest mt-1">
                    Upload rápido · qualquer fase
                  </p>
                </div>
              </div>
              <button onClick={fechar} className="text-slate-500 hover:text-white transition-all">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Tipo de documento
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {TIPOS_QUICK.map((t) => {
                    const doc = documentosAtuais[t.chave];
                    const jaEnviado = doc && (doc.status === 'enviado' || doc.status === 'pendente' || doc.file || doc.meta);
                    const selecionado = tipoEscolhido === t.chave;

                    return (
                      <button
                        key={t.chave}
                        type="button"
                        onClick={() => setTipoEscolhido(t.chave)}
                        className={`w-full p-3 rounded-xl border text-left transition-all flex items-center justify-between gap-3 ${
                          selecionado
                            ? 'bg-amber-400/10 border-amber-400 text-amber-400'
                            : jaEnviado
                              ? 'bg-slate-950 border-white/5 text-slate-400'
                              : 'bg-slate-950 border-white/10 text-white hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FileText
                            size={14}
                            className={
                              selecionado
                                ? 'text-amber-400'
                                : jaEnviado
                                  ? 'text-emerald-500'
                                  : 'text-slate-500'
                            }
                          />
                          <span className="text-[11px] font-black uppercase tracking-tight truncate">
                            {t.titulo}
                          </span>
                        </div>
                        {jaEnviado && (
                          <span className="text-[8px] font-black uppercase text-emerald-500 flex items-center gap-1 shrink-0">
                            <CheckCircle2 size={10} /> OK
                          </span>
                        )}
                        {selecionado && !jaEnviado && (
                          <span className="text-[8px] font-black uppercase text-amber-400 shrink-0">
                            Selecionado
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {tipoEscolhido && (
                <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                    Arquivo PDF
                  </label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => selecionarArquivo(e.target.files?.[0] || null)}
                  />
                  {!arquivo ? (
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="w-full border-2 border-dashed border-amber-400/30 hover:border-amber-400/60 rounded-xl py-5 transition-all flex flex-col items-center gap-2 bg-amber-400/5 hover:bg-amber-400/10"
                    >
                      <UploadCloud size={20} className="text-amber-400" />
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">
                        Selecionar PDF
                      </span>
                    </button>
                  ) : (
                    <div className="bg-slate-950 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-3">
                      <FileText size={16} className="text-emerald-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-white truncate">{arquivo.name}</p>
                        <p className="text-[8px] text-slate-500 font-black uppercase">
                          {(arquivo.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={() => setArquivo(null)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 transition-all"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {erro && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex gap-2">
                  <AlertTriangle size={14} className="shrink-0" />
                  <p className="text-[9px] font-black uppercase tracking-wide leading-relaxed">{erro}</p>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-white/5 bg-slate-950/50 flex gap-3 shrink-0">
              <button
                type="button"
                onClick={fechar}
                className="flex-1 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 text-[10px] font-black uppercase tracking-widest transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmar}
                disabled={!tipoEscolhido || !arquivo}
                className="flex-1 py-3 rounded-xl bg-amber-400 text-slate-950 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={12} /> Anexar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}